import {Store} from 'common/report/stores/store';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {Bookmark, ContentType, Dashboard, DashboardCard} from 'common/report/dashboards/types';
import type {BookmarksMap, BookmarkStore} from 'common/report/stores/bookmarks';

export interface DashboardsMap {
  [dashboardId: string]: Dashboard;
}

type DashboardStore = Store<DashboardsMap>;

// store for individual dashboards
export function createDashboardStore(): Store<DashboardsMap> {
  return new Store<DashboardsMap>({
    apiClient: dashboardsClient,
    defaultValue: {},
    name: `dashboard`,
    transform: {
      index: (dashboard: Dashboard) => ({[String(dashboard.id)]: dashboard}),
    },
  });
}

/**
 * Sets up value sharing such that when the DashboardStore's dashboard entity values are loaded/updated,
 * the bookmark entity values inside the dashboard are automatically loaded into the BookmarkStore offline.
 */
export function shareDashboardBookmarks(dashboardStore: DashboardStore, bookmarkStore: BookmarkStore): void {
  dashboardStore.shareValues<BookmarksMap>(
    // BookmarkStore's RawValue is BookmarksMap while shareValues expects
    // Store<..., unknown>; the RawValue side is unused by value sharing
    bookmarkStore as Store<BookmarksMap, unknown>,
    (valueUpdate): Array<{key: string; valueUpdate: BookmarksMap}> => {
      const sharedBookmarkValues: Array<{key: string; valueUpdate: BookmarksMap}> = [];
      for (const dashboardId in valueUpdate) {
        const dashboard = valueUpdate[dashboardId];
        if (!dashboard) {
          continue;
        }
        const cards =
          dashboard.cards?.filter(
            (c): c is DashboardCard => c.type === ContentType.Report || c.type === ContentType.ReportLink,
          ) ?? [];
        const cardBookmarks = cards.map((c) => c.bookmark);
        const bookmarks = dashboard.bookmarks ?? [];
        sharedBookmarkValues.push(
          // cards whose bookmark isn't hydrated yet carry an id-less placeholder
          // (INVALID_BOOKMARK), which must not be shared into the BookmarkStore
          ...[...bookmarks, ...cardBookmarks]
            .filter((b): b is Bookmark => b?.id !== undefined && b?.id !== null)
            .map((b) => {
              const key = b.id.toString();
              return {key, valueUpdate: {[key]: b}};
            }),
        );
      }
      return sharedBookmarkValues;
    },
  );
}
